import { Injectable } from "@angular/core";
import { of } from "rxjs";
import { catchError, take } from "rxjs/operators";
import { DialogService } from "src/app/shared/components/dialog/service/dialog.service";
import { CmsService } from "src/app/shared/service/cms/cms.service";
import { DateUtilService } from "src/app/shared/service/date-util/date-util.service";
import { StorageService } from "src/app/shared/service/storage/storage.service";
import { InformativeNoteComponent } from "../informative-note/informative-note.component";
import { InformativeNote } from "../model/informative-note.model";

// Recupera le note informative dal CMS, le filtra per finestra temporale, le ordina per
// `sort_order` e le mostra come modali in sequenza (chiusa una si apre la successiva),
// una sola volta per sessione. Mirror del "ragionamento" del banner CO-6905 ma via dialog.
@Injectable({
  providedIn: "root",
})
export class InformativeNotesService {
  private readonly SESSION_KEY = "informativeNotes";
  private readonly DATE_FORMAT = "YYYY-MM-DD HH:mm:ss";

  constructor(
    private cmsService: CmsService,
    private dialog: DialogService,
    private dateUtil: DateUtilService,
    private storageSrv: StorageService
  ) {}

  // Mostra le note una sola volta per sessione (guard su session storage).
  showInformativeNotesOncePerSession(isMobile: boolean): void {
    if (!!this.storageSrv.getSessionStorage(this.SESSION_KEY)) {
      return;
    }
    this.storageSrv.setSessionStorage(this.SESSION_KEY, true);

    this.cmsService
      .getInformativeNotes()
      .pipe(
        take(1),
        catchError(() => of([] as InformativeNote[]))
      )
      .subscribe((notes: InformativeNote[]) => {
        const visible = (notes || [])
          .filter((note) => this.isVisible(note))
          .sort((a, b) => a.sort_order - b.sort_order);
        this.openInSequence(visible, 0, isMobile);
      });
  }

  // Regola di visibilita' (spec CO-9067): mostrata se entrambe le date sono vuote, oppure
  // se entrambe sono valorizzate in formato corretto e "oggi" ricade nella finestra
  // [date_start, date_end]. Ogni caso parziale o con formato invalido non e' mai mostrato.
  private isVisible(note: InformativeNote): boolean {
    const start = note?.date_start;
    const end = note?.date_end;
    const bothEmpty = !start && !end;
    if (bothEmpty) {
      return true;
    }
    const bothSet = !!start && !!end;
    if (!bothSet) {
      return false;
    }
    if (
      !this.dateUtil.isValidDate(start, this.DATE_FORMAT) ||
      !this.dateUtil.isValidDate(end, this.DATE_FORMAT)
    ) {
      return false;
    }
    return (
      this.dateUtil.isDateBeforeToday(start, this.DATE_FORMAT) &&
      this.dateUtil.isDateAfterToday(end, this.DATE_FORMAT)
    );
  }

  // Apre la nota all'indice `index`; alla chiusura apre la successiva.
  private openInSequence(
    notes: InformativeNote[],
    index: number,
    isMobile: boolean
  ): void {
    if (index >= notes.length) {
      return;
    }
    this.dialog
      .onOpenDialogResponsive(isMobile, InformativeNoteComponent, notes[index])
      .pipe(take(1))
      .subscribe(() => this.openInSequence(notes, index + 1, isMobile));
  }
}
